<script setup lang='ts'>
import type {
  UploadFile,
  UploadInstance,
  UploadProps,
  UploadRequestOptions,
  UploadUserFile,
} from 'element-plus'
import type { MsUploadResource } from '../types/ms-upload'
import {
  useFormItem,
} from 'element-plus'
import { clone } from 'ramda'
import { isNotUndefined, isNumber, isUndefined } from 'ramda-adjunct'
import Sortable from 'sortablejs'
import { computed, nextTick, ref, watch } from 'vue'
import { useUploadFile } from '../composables/use-upload-file'
import {
  allFileExtension,
  getFileType,
  messageBoxConfirm,
  showErrorMessage,
  showSuccessMessage,
  showWarnMessage,
} from '../utils'

type MsUploadUserFile = UploadUserFile & { id: number }

export interface Props {
  modelValue: MsUploadResource[]
  tips?: string
  tipsSlot?: boolean
  limit?: number
  accept: string
  uploadText?: string
  limitSize?: number
  enablePreview?: boolean
  alias?: string
  action?: string
  listType?: UploadInstance['listType']
  tokenApiPath: string
  resourceApiPath: string
  autoCreateResource?: boolean
  dragSort?: boolean
}

interface Emits {
  (e: 'update:modelValue', value: MsUploadResource[]): void

  (e: 'change', value: MsUploadResource[]): void
}

const props = withDefaults(defineProps<Props>(), {
  tips: '',
  tipsSlot: false,
  limit: Number.MAX_SAFE_INTEGER,
  uploadText: '点击上传',
  limitSize: 10 * 1024 * 1024,
  enablePreview: true,
  alias: '',
  action: '',
  autoCreateResource: true,
  dragSort: false,
})

const emits = defineEmits<Emits>()

const fileListRef = ref<MsUploadResource[]>([])
const uploadRef = ref<UploadInstance>()
const fileList = ref<MsUploadUserFile[]>([])
const { formItem } = useFormItem()
const isUpdating = ref(false)

watch(() => props.modelValue, () => {
  fileListRef.value = props.modelValue ?? []
  fileList.value = props.modelValue?.map((it) => {
    return {
      name: it.name,
      url: it.url,
      id: it.id,
    }
  })
}, { immediate: true })

const pictureCard = computed(() => props.listType === 'picture-card')
const isDisabled = computed(() => fileList.value?.length >= props.limit)

function isFileTypeValid(fileName: string): boolean {
  const fileType = getFileType(fileName)
  return fileType !== '' && props.accept.includes(fileType.slice(1))
}

async function handleBeforeRemove(file: UploadFile) {
  if (file.size) {
    if (file.size > props.limitSize) return true
  }
  if (!isFileTypeValid(file.name)) {
    return true
  }
  return messageBoxConfirm({ message: `确定移除 ${file.name}？` })
}

const handleRemove: UploadProps['onRemove'] = (uploadFile) => {
  if (uploadFile.status === 'success') {
    fileListRef.value = props.modelValue.filter(it => it.url !== uploadFile.url)
    updateValue()
  }
}

const handleSuccess: UploadProps['onSuccess'] = () => {
  const allFileComplete = fileList.value.every((it) => {
    if (isUndefined(it.status)) {
      return true
    }
    return !(it.status === 'ready' || it.status === 'uploading')
  })
  if (allFileComplete) {
    fileListRef.value = fileList.value.map((fileItem) => {
      if (isNotUndefined(fileItem.response)) {
        return fileItem.response as MsUploadResource
      }
      return fileListRef.value.find(it => fileItem.id === it.id) as MsUploadResource
    })
    showSuccessMessage('上传成功')
    updateValue()
  }
}

const handleError: UploadProps['onError'] = () => {
  isUpdating.value = false
}

const handleBeforeUpload: UploadProps['beforeUpload'] = (file) => {
  if (file.size > props.limitSize) {
    showErrorMessage(`单个文件大小不能超过${Math.ceil(props.limitSize / 1024 / 1024)}M!`)
    return false
  }
  if (!isFileTypeValid(file.name)) {
    showErrorMessage(`文件格式不正确!`)
    return false
  }
  return true
}

function handleExceed() {
  console.log('limit===>', props.limit)
  return showWarnMessage(`最多上传${props.limit}个文件! 本次上传超出数量限制。`)
}

function updateValue() {
  isUpdating.value = false
  emits('update:modelValue', clone(fileListRef.value))
  emits('change', clone(fileListRef.value))
  formItem?.validate('change').catch(err => console.warn(err))
}

const { toUploadFile } = useUploadFile(props.alias, props.tokenApiPath, props.resourceApiPath, props.autoCreateResource)

function uploadFile(options: UploadRequestOptions) {
  isUpdating.value = true
  toUploadFile(options)
}

const previewImages = ref<string[]>([])
const previewIndex = ref(0)

function handlePreview(file: UploadFile) {
  if (!props.enablePreview || !file.url) return
  const url = file.url
  if (allFileExtension.image.includes(getFileType(url))) {
    previewImages.value = fileListRef.value
      .map(it => it.url)
      .filter(url => allFileExtension.image.includes(getFileType(url)))
    previewIndex.value = previewImages.value.indexOf(url)
    return
  }
  window.open(url)
}

nextTick()
  .then(() => {
    if (props.dragSort) {
      const el = uploadRef.value?.$el.querySelector('.el-upload-list')
      if (!el) return
      Sortable.create(el, {
        onEnd: ({ newIndex, oldIndex }) => {
          if (isNumber(newIndex) && isNumber(oldIndex)) {
            const temp = clone(props.modelValue)
            const currentItem = temp.splice(oldIndex, 1)[0]
            temp.splice(newIndex, 0, currentItem)
            fileListRef.value = temp
            updateValue()
          }
        },
      })
    }
  })

defineExpose({
  isUpdating,
})
</script>

<template>
  <div>
    <el-upload
      ref="uploadRef"
      v-bind="$attrs"
      v-model:file-list="fileList"
      :class="{
        'upload-disabled': isDisabled,
        'is-drag-sort': dragSort,
      }"
      :action="action"
      :list-type="listType as UploadInstance['listType']"
      :accept="accept"
      :limit="limit"
      :multiple="limit > 1"
      :before-remove="handleBeforeRemove"
      :before-upload="handleBeforeUpload"
      :on-exceed="handleExceed"
      :on-remove="handleRemove"
      :on-preview="handlePreview"
      :on-success="handleSuccess"
      :on-error="handleError"
      :http-request="uploadFile"
    >
      <template v-if="isDisabled">
        <el-button v-if="!pictureCard" type="primary" size="small" disabled>
          <div class="text-bold">
            {{ uploadText }}
          </div>
        </el-button>
      </template>
      <template #trigger>
        <template v-if="!isDisabled">
          <slot v-if="pictureCard" name="picture-card-trigger" />
          <el-button v-else type="primary" size="small">
            <div class="text-bold">
              {{ uploadText }}
            </div>
          </el-button>
        </template>
      </template>
      <template #tip>
        <div v-if="tips" class="tips">
          {{ tips }}
        </div>
        <slot v-if="tipsSlot" name="tipsSlot" />
      </template>
    </el-upload>
    <el-image-viewer
      v-if="previewImages.length" :initial-index="previewIndex" :url-list="previewImages"
      hide-on-click-modal teleported @close="previewImages = []"
    />
  </div>
</template>

<style lang='scss' scoped>
.tips {
  font-size: $font-size-sm;
  line-height: 18px;
  padding-top: $space-sm;
  color: $font-color-secondary;
}

.upload-disabled {
  ::v-deep(.el-upload--picture-card) {
    display: none !important;
  }
}

::v-deep(.el-upload-list--picture-card) {
  display: flex !important;

  .el-upload-list__item-actions span + span {
    margin-left: 0.3rem;
  }
}

::v-deep(.el-upload-list__item-status-label) {
  display: none !important;
}

::v-deep(.el-upload-list__item) {
  transition: none;
}

::v-deep(.is-drag-sort .el-upload-list__item .el-upload-list__item-actions) {
  cursor: move;
}
</style>
